import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { gallery } from '../mockData';

export const Gallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (selectedImage) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.overflow = 'hidden';
      
      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [selectedImage]);

  const Modal = () => {
    if (!selectedImage) return null;

    return createPortal(
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'rgba(0, 0, 0, 0.98)',
          zIndex: 999999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '16px',
          overflow: 'auto'
        }}
        onClick={() => setSelectedImage(null)}
      >
        {/* Close Button */}
        <button
          onClick={() => setSelectedImage(null)}
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            width: '64px',
            height: '64px',
            backgroundColor: '#dc2626',
            borderRadius: '50%',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000001,
            boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#b91c1c';
            e.currentTarget.style.transform = 'scale(1.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#dc2626';
            e.currentTarget.style.transform = 'scale(1)';
          }}
        >
          <X style={{ width: '40px', height: '40px', color: 'white', strokeWidth: 3 }} />
        </button>

        {/* Content */}
        <div
          style={{
            width: '100%',
            maxWidth: '1200px',
            marginTop: '80px',
            zIndex: 1000000
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <h3
            style={{
              color: 'white',
              fontSize: window.innerWidth < 640 ? '20px' : '32px',
              fontWeight: '900',
              marginBottom: '24px',
              textAlign: 'center'
            }}
          >
            {selectedImage.title}
          </h3>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: window.innerWidth < 640 ? '1fr' : '1fr 1fr',
              gap: '16px'
            }}
          >
            {/* Before */}
            <div style={{ position: 'relative' }}>
              <div
                style={{
                  position: 'absolute',
                  top: '12px',
                  left: '12px',
                  backgroundColor: '#dc2626',
                  color: 'white',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  fontWeight: 'bold',
                  fontSize: '16px',
                  zIndex: 10
                }}
              >
                Před
              </div>
              <img
                src={selectedImage.before}
                alt="Před"
                style={{
                  width: '100%',
                  height: 'auto',
                  borderRadius: '16px',
                  boxShadow: '0 20px 60px rgba(0,0,0,0.5)'
                }}
              />
            </div>

            {/* After */}
            <div style={{ position: 'relative' }}>
              <div
                style={{
                  position: 'absolute',
                  top: '12px',
                  left: '12px',
                  backgroundColor: '#16a34a',
                  color: 'white',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  fontWeight: 'bold',
                  fontSize: '16px',
                  zIndex: 10
                }}
              >
                Po
              </div>
              <img
                src={selectedImage.after}
                alt="Po"
                style={{
                  width: '100%',
                  height: 'auto',
                  borderRadius: '16px',
                  boxShadow: '0 20px 60px rgba(0,0,0,0.5)'
                }}
              />
            </div>
          </div>
        </div>
      </div>,
      document.body
    );
  };

  return (
    <section id="gallery" className="py-12 sm:py-20 md:py-24 bg-gradient-to-b from-white via-gray-50 to-white relative overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute top-1/2 right-0 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header - Enhanced */}
        <div className="text-center mb-12 sm:mb-16 md:mb-20">
          <div className="inline-block mb-3 sm:mb-4">
            <span className="px-4 sm:px-6 py-1.5 sm:py-2 rounded-full bg-gradient-to-r from-blue-100 to-cyan-100 text-blue-600 font-semibold text-xs sm:text-sm tracking-wider uppercase">
              Portfolio
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-gray-900 mb-4 sm:mb-6 bg-gradient-to-r from-gray-900 via-blue-900 to-gray-900 bg-clip-text text-transparent px-4">
            Moje práce
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed px-4">
            Podívejte se na výsledky mého profesionálního čištění
          </p>
        </div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {gallery.map((item, index) => (
            <div
              key={item.id}
              className="group relative cursor-pointer transform hover:-translate-y-2 transition-all duration-500"
              onClick={() => setSelectedImage(item)}
              style={{
                animationDelay: `${index * 50}ms`
              }}
            >
              <div className="relative overflow-hidden rounded-xl sm:rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500 bg-white">
                <div className="relative aspect-[4/3] bg-gray-200">
                  <img
                    src={item.before}
                    alt={`${item.title} - Před`}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <img
                    src={item.after}
                    alt={`${item.title} - Po`}
                    className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-700"
                  />
                  
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-900/95 via-gray-900/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    <div className="absolute bottom-3 sm:bottom-4 left-3 sm:left-4 right-3 sm:right-4">
                      <h3 className="text-white font-black text-sm sm:text-lg mb-1 sm:mb-2">{item.title}</h3>
                      <p className="text-blue-300 text-xs sm:text-sm font-semibold flex items-center gap-2">
                        <span className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></span>
                        Klikněte pro zvětšení
                      </p>
                    </div>
                  </div>

                  <div className="absolute top-2 left-2 sm:top-3 sm:left-3 backdrop-blur-xl bg-gradient-to-r from-blue-600/90 to-cyan-600/90 text-white px-2 py-1 sm:px-3 sm:py-1.5 rounded-lg text-xs font-bold shadow-xl border border-white/20">
                    Před / Po
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Modal using Portal */}
        <Modal />
      </div>
    </section>
  );
};
